import { CloseOutlined } from '@ant-design/icons';
import { Popconfirm, Radio, Spin, Tag, Tooltip, Typography } from 'antd';
import { addDays, format, startOfWeek } from 'date-fns';
import type React from 'react';
import dayjs from '@/utils/dayjs-instance';
import { DistrictDeleteType } from './useCalendarState';

const getWeekdayByIndex = (index: number) => {
  const start = startOfWeek(new Date(), { weekStartsOn: 1 });
  const day = addDays(start, index);
  return format(day, 'EEEE');
};

const getRemoveDistrictDescription = (
  bulkDeleteType: DistrictDeleteType,
  dayIndex: number,
  slotTime: string
): string => {
  switch (bulkDeleteType) {
    case DistrictDeleteType.SINGLE:
      return `on ${getWeekdayByIndex(dayIndex)} at ${slotTime}`;
    case DistrictDeleteType.WEEK:
      return `for the entire time slot at ${slotTime}`;
    case DistrictDeleteType.COLUMN:
      return `for the entire day on ${getWeekdayByIndex(dayIndex)}`;
    case DistrictDeleteType.ALL:
      return 'from the entire schedule';
    default:
      return '';
  }
};

const formatDate = (date: string) => dayjs(date).format('YYYY-MM-DD');

type TagColor = 'red' | 'default' | 'gray' | 'orange' | 'blue';

const buildTempDateRangeFragment = (
  tempDateFrom: string | null,
  tempDateTo: string | null
): string => {
  if (tempDateFrom && tempDateTo) {
    return ` from ${formatDate(tempDateFrom)} to ${formatDate(tempDateTo)}`;
  }
  if (tempDateFrom) {
    return ` from ${formatDate(tempDateFrom)}`;
  }
  if (tempDateTo) {
    return ` until ${formatDate(tempDateTo)}`;
  }
  return '';
};

export const getDistrictTagTooltip = (params: {
  hasNoProviders: boolean;
  enabled: boolean;
  isTempDistrict: boolean;
  tempDateFrom: string | null;
  tempDateTo: string | null;
}): string => {
  const { hasNoProviders, enabled, isTempDistrict, tempDateFrom, tempDateTo } = params;
  const tempDateRangeFragment = buildTempDateRangeFragment(tempDateFrom, tempDateTo);

  if (hasNoProviders) return 'No active delivery services in this district.';
  if (!enabled && isTempDistrict) {
    const operatesPart = tempDateRangeFragment
      ? ` Also, this district operates only${tempDateRangeFragment}.`
      : ' This district has temporary scheduling.';
    return `This district is disabled. You can enable it in the district list.${operatesPart}`;
  }
  if (!enabled) return 'This district is disabled. You can enable it in the district list.';
  if (isTempDistrict) {
    return tempDateRangeFragment
      ? `This district operates only${tempDateRangeFragment}.`
      : 'This district is temporary.';
  }
  return '';
};

export const getDistrictTagColor = (params: {
  isDisabled: boolean;
  hasNoProviders: boolean;
  enabled: boolean;
  isTempDistrict: boolean;
}): TagColor => {
  const { isDisabled, hasNoProviders, enabled, isTempDistrict } = params;
  if (isDisabled) return 'red';
  if (hasNoProviders) return 'default';
  if (!enabled) return 'gray';
  if (isTempDistrict) return 'orange';
  return 'blue';
};

interface DistrictTagProps {
  district: Record<string, unknown>;
  loadingDistrictId: string | null;
  districtDeleteType: DistrictDeleteType;
  slot: Record<string, unknown>;
  dayIndex: number;
  dayDate: Date;
  handleRemoveDistrict: (districtId: string, slotId: number, dayIndex: number) => Promise<void>;
  setBulkDeleteType: (value: DistrictDeleteType) => void;
  isDisabled: boolean;
}

export const DistrictTag: React.FC<DistrictTagProps> = ({
  district,
  loadingDistrictId,
  districtDeleteType,
  slot,
  dayIndex,
  dayDate,
  handleRemoveDistrict,
  setBulkDeleteType,
  isDisabled,
}) => {
  const isTempDistrict = Boolean(
    (district.tempDateFrom as string) || (district.tempDateTo as string)
  );

  const isWithinTempDateRange = (() => {
    if (!isTempDistrict || !dayDate) return false;
    const from = district.tempDateFrom as string | null;
    const to = district.tempDateTo as string | null;
    const current = dayjs(dayDate).startOf('day');
    if (from && to) {
      return current.isBetween(dayjs(from), dayjs(to), 'day', '[]');
    }
    if (from) return current.isSameOrAfter(dayjs(from), 'day');
    if (to) return current.isSameOrBefore(dayjs(to), 'day');
    return true;
  })();

  const districtData = district.district as Record<string, unknown>;
  const providers = districtData.district_delivery_providers as unknown[] | undefined | null;
  const hasNoProviders = !providers || providers.length === 0;
  const enabled = Boolean(districtData.enabled);

  const tooltipTitle = getDistrictTagTooltip({
    hasNoProviders,
    enabled,
    isTempDistrict,
    tempDateFrom: district.tempDateFrom as string | null,
    tempDateTo: district.tempDateTo as string | null,
  });

  const tagColor = getDistrictTagColor({
    isDisabled,
    hasNoProviders,
    enabled,
    isTempDistrict,
  });

  if (isTempDistrict && !isWithinTempDateRange) {
    return null;
  }

  return (
    <Tooltip trigger={'hover'} zIndex={1} color={'blue'} title={tooltipTitle}>
      <Tag
        key={district.districtId as string}
        closable={loadingDistrictId !== (district.id as string)}
        color={tagColor}
        onClose={(e) => {
          e.preventDefault();
        }}
        closeIcon={
          <Popconfirm
            icon={null}
            title={
              <div style={{ marginTop: 10 }}>
                <Typography.Paragraph>
                  Are you sure you want to remove <strong>{districtData.name as string}</strong>?
                </Typography.Paragraph>
                <Typography.Paragraph>
                  The district will be removed{' '}
                  <strong>
                    {getRemoveDistrictDescription(
                      districtDeleteType,
                      dayIndex,
                      slot.timeDuration as string
                    )}
                  </strong>
                  .
                </Typography.Paragraph>
                <Radio.Group
                  onChange={(e) => setBulkDeleteType(e.target.value)}
                  value={districtDeleteType}
                >
                  <Tooltip
                    title="Removes the district only from the selected slot inside time slot"
                    placement="top"
                  >
                    <Radio value={DistrictDeleteType.SINGLE}>Slot</Radio>
                  </Tooltip>
                  <Tooltip
                    title="Removes the district for the entire time slot (horizontal)"
                    placement="top"
                  >
                    <Radio value={DistrictDeleteType.WEEK}>Time slot</Radio>
                  </Tooltip>
                  <Tooltip
                    title="Removes the district for the entire day (vertical)"
                    placement="top"
                  >
                    <Radio value={DistrictDeleteType.COLUMN}>Entire day</Radio>
                  </Tooltip>
                  <Tooltip title="Removes the district from the entire schedule" placement="top">
                    <Radio value={DistrictDeleteType.ALL}>Entire Schedule</Radio>
                  </Tooltip>
                </Radio.Group>
              </div>
            }
            onConfirm={async () => {
              await handleRemoveDistrict(
                district.districtId as string,
                slot.id as number,
                dayIndex
              );
            }}
            okText="Yes"
            cancelText="No"
            disabled={loadingDistrictId === (district.id as string)}
          >
            <CloseOutlined />
          </Popconfirm>
        }
        style={{
          whiteSpace: 'normal',
        }}
      >
        {districtData.name as string}
        <Spin spinning={loadingDistrictId === (district.id as string)} size="small" />
      </Tag>
    </Tooltip>
  );
};
